import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useCallback, useContext, useState } from "react";
import { Feature } from "ol";
import { IEsriDomain } from "../Map/rest";
import { IDocument, IRelatedDocs } from "../interfaces";
import { useNomaContext } from "./MapContext";
import { config, IDataTab } from "../Map/config";
import { getThumbnailUrl } from "../Providers/imageProvider";
import { getRelatedDocsUrls } from "../Providers/docsProvider";
import { useFiltersContext } from "./FiltersContext";

interface ITabName {
    name: string;
    code: number;
}

interface IImageUrl {
    objectId: number;
    url: string;
}

interface IField {
    name: string;
    alias: string;
    type: string;
    domain: IEsriDomain;
}

export interface ICustomViewContext {
    hidden: boolean;
    setHidden: Dispatch<SetStateAction<boolean>>;
    tabNames: ITabName[];
    setTabs: () => void;
    fieldNames: string[];
    setFieldNames: () => void;
    tabData: Feature[];
    setTabData: Dispatch<SetStateAction<Feature[]>>;
    tabConfig: IDataTab;
    relatedDocs: IRelatedDocs[];
    setRelatedDocs: (features: Feature[]) => void;
    moveEndTime: number;
    setMoveEndTime: Dispatch<SetStateAction<number>>;
    images: IImageUrl[];
    setImages: (features: Feature[]) => void;
    changeTab: React.Dispatch<React.SetStateAction<number>>;
}

const CustomViewContext = createContext<ICustomViewContext>(null);

export const useCustomViewContext = (): ICustomViewContext => useContext(CustomViewContext);

export const CustomViewContextProvider: FC = (props: React.PropsWithChildren<ICustomViewContext>) => {
    const { isIFrame, activeTab, fieldInfos } = useNomaContext();
    const { setTabFilter } = useFiltersContext();

    const [hidden, setHidden] = useState<boolean>(isIFrame);
    const [tabNames, setTabs] = useState<ITabName[]>([]);
    const [fieldNames, setFieldNames] = useState<string[]>([]);
    const [tabData, setTabData] = useState<Feature[]>([]);
    const [tabConfig, setTabConfig] = useState<IDataTab>();
    const [relatedDocs, setRelatedDocs] = useState<IRelatedDocs[]>([]);
    const [moveEndTime, setMoveEndTime] = useState<number>(0);
    const [images, setImages] = useState<IImageUrl[]>([]);

    const setTabNames = useCallback(() => {
        const fieldDomain =
            fieldInfos && fieldInfos["0"].find((field: IField) => field.name === config.dataView.tabField);
        const codedValues = fieldDomain && fieldDomain.domain.type === "codedValue";
        if (!codedValues) {
            setTabs([]);
            return;
        }
        fieldDomain.domain.codedValues.sort((a, b) => Number(a.code) - Number(b.code));
        setTabs(fieldDomain.domain.codedValues as ITabName[]);
    }, [fieldInfos]);

    const setCurrentFieldNames = useCallback(() => {
        const tabConfig = config.dataView.tabs.find((tab) => tab.tabValue === activeTab);
        if (!tabConfig || !tabConfig.fields) {
            return;
        }

        const newFieldNames: string[] = tabConfig.fields.reduce((newFieldNames, tabField) => {
            const field = fieldInfos && fieldInfos["0"].find((f) => f.name === tabField);
            field && newFieldNames.push(tabField);

            return newFieldNames;
        }, []);
        setFieldNames(newFieldNames);
        setTabConfig(tabConfig);
    }, [activeTab, fieldInfos]);

    const fetchImageUrls = useCallback(
        (features: Feature[]) => {
            if (!features?.length) {
                return;
            }

            fetchRelatedImages(features);
        },
        [images]
    );

    const fetchRelatedImages = async (features: Feature[]) => {
        for (const feature of features) {
            const url = await getThumbnailUrl(feature.get("OBJECTID"));
            if (url && !images.find((img: IImageUrl) => img.objectId === feature.get("OBJECTID"))) {
                const newState = images;
                newState.push({ objectId: feature.get("OBJECTID"), url });
                setImages(newState);
                setMoveEndTime(Date.now());
            }
        }
    };

    const fetchRelatedDocs = async (features: Feature[]) => {
        if (!tabConfig?.showDocs || !features) {
            return;
        }
        for (const feature of features) {
            const objectId = feature.get("OBJECTID");
            const docs = await getRelatedDocsUrls(objectId);
            if (docs && docs.length && relatedDocs && !relatedDocs.find((item) => item.objectId === objectId)) {
                const newState = relatedDocs;
                newState.push({ objectId, docs });
                setRelatedDocs(newState);
                setMoveEndTime(Date.now());
            }
        }
    };

    const values = {
        hidden,
        setHidden,
        tabNames,
        setTabs: setTabNames,
        fieldNames,
        setFieldNames: setCurrentFieldNames,
        tabData,
        setTabData,
        tabConfig,
        relatedDocs,
        setRelatedDocs: fetchRelatedDocs,
        moveEndTime,
        setMoveEndTime,
        images,
        setImages: fetchImageUrls,
        changeTab: setTabFilter
    };
    return <CustomViewContext.Provider value={values}>{props.children}</CustomViewContext.Provider>;
};
